import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    connect() {
        this.boundFinish = this.finish.bind(this)
        this.boundPageLoaded = this.pageLoaded.bind(this)
        this.boundRequestStarted = this.requestStarted.bind(this)
        this.boundRequestFinished = this.requestFinished.bind(this)
        this.activeRequests = 0
        this.pageIsLoading = document.readyState !== "complete"

        this.installXhrTracking()
        this.installFetchTracking()

        document.addEventListener("turbo:before-fetch-request", this.boundRequestStarted)
        document.addEventListener("turbo:before-fetch-response", this.boundRequestFinished)
        document.addEventListener("turbo:fetch-request-error", this.boundRequestFinished)
        window.addEventListener("pageshow", this.boundPageLoaded)

        if (this.pageIsLoading) {
            window.addEventListener("load", this.boundPageLoaded, { once: true })
        } else {
            requestAnimationFrame(this.boundFinish)
        }
    }

    disconnect() {
        clearTimeout(this.hideTimer)
        this.restoreXhrTracking()
        this.restoreFetchTracking()
        document.removeEventListener("turbo:before-fetch-request", this.boundRequestStarted)
        document.removeEventListener("turbo:before-fetch-response", this.boundRequestFinished)
        document.removeEventListener("turbo:fetch-request-error", this.boundRequestFinished)
        window.removeEventListener("pageshow", this.boundPageLoaded)
        window.removeEventListener("load", this.boundPageLoaded)
    }

    installXhrTracking() {
        this.xhrPrototype = window.XMLHttpRequest?.prototype
        if (!this.xhrPrototype) return

        const controller = this
        this.originalXhrSend = this.xhrPrototype.send
        this.trackedXhrSend = function (...args) {
            controller.requestStarted()
            this.addEventListener("loadend", controller.boundRequestFinished, { once: true })

            try {
                return controller.originalXhrSend.apply(this, args)
            } catch (error) {
                this.removeEventListener("loadend", controller.boundRequestFinished)
                controller.requestFinished()
                throw error
            }
        }
        this.xhrPrototype.send = this.trackedXhrSend
    }

    restoreXhrTracking() {
        if (this.xhrPrototype?.send === this.trackedXhrSend) {
            this.xhrPrototype.send = this.originalXhrSend
        }
    }

    installFetchTracking() {
        if (!window.fetch) return

        const controller = this
        this.originalFetch = window.fetch
        this.trackedFetch = function (...args) {
            controller.requestStarted()

            try {
                return controller.originalFetch.apply(this, args).finally(controller.boundRequestFinished)
            } catch (error) {
                controller.requestFinished()
                throw error
            }
        }
        window.fetch = this.trackedFetch
    }

    restoreFetchTracking() {
        if (window.fetch === this.trackedFetch) {
            window.fetch = this.originalFetch
        }
    }

    requestStarted() {
        this.activeRequests += 1
        if (this.activeRequests === 1) this.start()
    }

    requestFinished() {
        this.activeRequests = Math.max(0, this.activeRequests - 1)
        this.finishWhenIdle()
    }

    pageLoaded() {
        this.pageIsLoading = false
        this.finishWhenIdle()
    }

    finishWhenIdle() {
        if (!this.pageIsLoading && this.activeRequests === 0) this.finish()
    }

    start() {
        if (this.element.classList.contains("global-page-progress--loading") &&
            !this.element.classList.contains("global-page-progress--complete")) return

        clearTimeout(this.hideTimer)
        this.element.classList.remove("global-page-progress--loading", "global-page-progress--complete")
        void this.element.offsetWidth
        this.element.classList.add("global-page-progress--loading")
    }

    finish() {
        if (!this.element.classList.contains("global-page-progress--loading")) return

        this.element.classList.add("global-page-progress--complete")
        this.hideTimer = setTimeout(() => {
            this.element.classList.remove("global-page-progress--loading", "global-page-progress--complete")
        }, 220)
    }
}
